import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useUserPreferences } from '../user-preferences';

import type { FeatureFlagDefaults, FeatureFlagSwitcherConfig } from './types';

type ExampleFeatureFlagContextValue = {
	/**
	 * Per-example defaults (from featureFlagDefaults prop). Applied before switcher overrides.
	 */
	defaultOverrides: FeatureFlagDefaults;
	/**
	 * User toggles from the Styles popover. When "Apply to all" is on, stored in user preferences and shared across examples.
	 */
	switcherOverrides: Record<string, boolean>;
	setSwitcherFlag: (flagKey: string, value: boolean) => void;
};

const ExampleFeatureFlagContext = createContext<ExampleFeatureFlagContextValue | null>(null);

function getDefaultSwitcherOverrides(
	configs: FeatureFlagSwitcherConfig[],
): Record<string, boolean> {
	return Object.fromEntries(
		configs.map((config) => [config.flagKey, config.defaultValue ?? false]),
	);
}

type ExampleFeatureFlagProviderProps = {
	/**
	 * Flags to apply for this example (no UI). Overrides page-level config for this example.
	 */
	featureFlagDefaults?: FeatureFlagDefaults;
	/**
	 * When set, shows toggles in the Styles popover; user choice overrides default for those flags.
	 */
	featureFlagSwitchers?: FeatureFlagSwitcherConfig[];
	children: React.ReactNode;
};

/**
 * Single provider for example-level feature flags:
 * - featureFlagDefaults: applied to this example (replaces need for path-based page config).
 * - featureFlagSwitchers: same idea but with a toggle in the Styles popover so the user can change the value.
 *
 * When "Apply to all components" is on, FF changes are stored in user preferences (exampleFeatureFlags) and shared across examples.
 * Otherwise each example has independent switcher state.
 */
export function ExampleFeatureFlagProvider({
	featureFlagDefaults = {},
	featureFlagSwitchers = [],
	children,
}: ExampleFeatureFlagProviderProps): React.JSX.Element {
	const defaults = useMemo(
		() => getDefaultSwitcherOverrides(featureFlagSwitchers),
		[featureFlagSwitchers],
	);
	const [switcherOverrides, setSwitcherOverridesState] = useState<Record<string, boolean>>(
		() => defaults,
	);

	const { userPreferences, setUserPreferences } = useUserPreferences();
	const isApplyAll = userPreferences['exampleSyncSettings'] === 'true';
	const storedFlags = userPreferences['exampleFeatureFlags'];

	const setSwitcherFlag = useCallback(
		(flagKey: string, value: boolean) => {
			setSwitcherOverridesState((prev) => ({ ...prev, [flagKey]: value }));
			if (isApplyAll) {
				setUserPreferences({
					exampleFeatureFlags: { ...(storedFlags ?? {}), [flagKey]: value },
				});
			}
		},
		[isApplyAll, setUserPreferences, storedFlags],
	);

	// When "Apply to all" is on, use user preferences; otherwise use local state.
	const effectiveSwitcherOverrides = useMemo(() => {
		if (!isApplyAll) return switcherOverrides;
		const merged = { ...switcherOverrides };
		for (const config of featureFlagSwitchers) {
			if (storedFlags != null && config.flagKey in storedFlags) {
				merged[config.flagKey] = storedFlags[config.flagKey];
			}
		}
		return merged;
	}, [isApplyAll, switcherOverrides, featureFlagSwitchers, storedFlags]);

	const value = useMemo(
		() => ({
			defaultOverrides: featureFlagDefaults,
			switcherOverrides: effectiveSwitcherOverrides,
			setSwitcherFlag,
		}),
		[featureFlagDefaults, effectiveSwitcherOverrides, setSwitcherFlag],
	);

	return (
		<ExampleFeatureFlagContext.Provider value={value}>
			{children}
		</ExampleFeatureFlagContext.Provider>
	);
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @repo/internal/react/require-jsdoc
export function useExampleFeatureFlags(): ExampleFeatureFlagContextValue | null {
	return useContext(ExampleFeatureFlagContext);
}
